import { env } from '@/env';
import {
  SESSION_REPLAY_BLOCK_SELECTORS,
  SESSION_REPLAY_MASK_TEXT_SELECTORS,
  SESSION_REPLAY_SAMPLE_PERCENT,
} from './sessionReplayConfig';
import { scrubProperties } from './mixpanelScrub';
import { reportError } from '@/services/datadog/reporter';
import mixpanel, { Callback, Dict, RequestOptions } from 'mixpanel-browser';
import { ENTRY_SOURCES, type EntrySource } from '@/consts/events';

const SHARED_COOKIE_DOMAIN = '.happyhearts.com';

// Product identifier for filtering in Mixpanel.
// Do NOT use the key `source` — that is reserved for CTA origin per Access taxonomy
// (welcome, header, medicare_coverage, gating stage, …).
const APP = 'access-registration';

// Pre-RND-37449 super-property keys (source/appVersion/userId). Persisted in the
// Mixpanel cookie for anyone who loaded an older bundle. Outside the shared
// cookie tier they are unregistered on init — see the comment at the
// unregister() call below. In the shared tier the pre-init cookie expiry is
// what removes them instead, since unregister() would write into the cookie
// Webflow also persists to.
const LEGACY_SUPER_PROPERTIES = ['source', 'appVersion', 'userId'] as const;

const MIXPANEL_DEBUG = env.MIXPANEL_DEBUG === 'true';

export const ENTRY_SOURCE_STORAGE_KEY = 'webregEntrySource';

const isEntrySource = (value: string | null): value is EntrySource =>
  Object.values(ENTRY_SOURCES).some((source) => source === value);

const readEntrySource = (): EntrySource | undefined => {
  try {
    const stored = sessionStorage.getItem(ENTRY_SOURCE_STORAGE_KEY);
    return isEntrySource(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
};

const writeEntrySource = (source: EntrySource): void => {
  try {
    sessionStorage.setItem(ENTRY_SOURCE_STORAGE_KEY, source);
  } catch {
    return;
  }
};

const reportMixpanelError = (operation: string, error: unknown): void => {
  reportError(
    new Error(`Mixpanel ${operation} failed: ${error instanceof Error ? error.name : typeof error}`),
  );
};

class AnalyticsManager {
  isInitialized = false;

  private language: string | undefined;

  private entrySource: EntrySource | undefined = readEntrySource();

  constructor() {
    if (env.MIXPANEL_TOKEN) {
      const isSharedCookieTier = env.MODE === 'production';

      if (isSharedCookieTier) {
        const legacyCookieName = `mp_${env.MIXPANEL_TOKEN}_mixpanel`;
        document.cookie = `${legacyCookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      }

      mixpanel.init(env.MIXPANEL_TOKEN, {
        api_host: env.MIXPANEL_API_HOST,
        debug: MIXPANEL_DEBUG,
        ignore_dnt: false,
        cookie_domain: isSharedCookieTier ? SHARED_COOKIE_DOMAIN : window.location.hostname,
        secure_cookie: window.location.protocol === 'https:',
        record_sessions_percent: SESSION_REPLAY_SAMPLE_PERCENT,
        record_block_selector: SESSION_REPLAY_BLOCK_SELECTORS.join(', '),
        record_console: true,
        record_mask_all_text: false,
        record_mask_text_selector: [...SESSION_REPLAY_MASK_TEXT_SELECTORS],
        record_mask_all_inputs: true,
        record_canvas: false,
        record_collect_fonts: false,
        record_heatmap_data: false,
      });

      // mixpanel.register is additive and persists in the cookie/localStorage —
      // only reset() clears super properties wholesale. A returning visitor who
      // loaded a pre-rename bundle therefore still carries the legacy keys, and
      // a stale `source: 'access-registration'` would override the CTA-origin
      // `source` on every event that doesn't set one. Drop them explicitly,
      // outside the shared tier — see the comment at LEGACY_SUPER_PROPERTIES.
      if (!isSharedCookieTier) {
        LEGACY_SUPER_PROPERTIES.forEach((key) => mixpanel.unregister(key));
      }

      this.registerBaseSuperProperties();

      this.isInitialized = true;
    }
  }

  // Super properties included on every event. Extracted so reset() can restore
  // them — mixpanel.reset() clears super properties wholesale, and `app` is the
  // key every board filters on, so losing it would be silent and total.
  // reset()'s only caller is the conditional in Start Over, and only once the
  // session is identified — see ANALYTICS.md for why an unconditional reset
  // there would wipe the shared cross-domain cookie (RND-39199).
  private registerBaseSuperProperties() {
    mixpanel.register(
      {
        app: APP,
        app_version: env.APP_VERSION,
        environment: env.MODE,
        entry_source: this.entrySource ?? ENTRY_SOURCES.DIRECT,
        ...(this.language ? { language: this.language } : {}),
      },
      { persistent: false },
    );
  }

  setLanguage(language: string) {
    this.language = language;
    if (this.isInitialized) {
      mixpanel.register({ language }, { persistent: false });
    }
  }

  setEntrySource(source: EntrySource) {
    this.entrySource = source;
    writeEntrySource(source);
    if (this.isInitialized) {
      mixpanel.register({ entry_source: source }, { persistent: false });
    }
  }

  setUserProperties(properties: Dict) {
    if (this.isInitialized) {
      mixpanel.register_once(scrubProperties(properties), 'None', { persistent: false });
    }
  }

  identifyUser(analyticsID: string, promoCode?: string) {
    if (!this.isInitialized || !analyticsID) return;
    try {
      mixpanel.identify(analyticsID);
      if (promoCode) this.setPromoCode(promoCode);
      mixpanel.people.set({ analyticsID, ...(promoCode ? { promoCode } : {}) });
    } catch (error) {
      reportMixpanelError('identify', error);
    }
  }

  setPromoCode(promoCode: string | undefined) {
    if (!this.isInitialized) return;
    try {
      if (promoCode) {
        mixpanel.register({ promo_code: promoCode }, { persistent: false });
      } else {
        mixpanel.unregister('promo_code', { persistent: false });
      }
    } catch (error) {
      reportMixpanelError('promo_code', error);
    }
  }

  isIdentified(): boolean {
    return this.isInitialized && mixpanel.get_property('$user_id') !== undefined;
  }

  track(
    eventName: string,
    properties: Dict = {},
    options: RequestOptions = {},
    callback: Callback = () => {},
  ) {
    if (this.isInitialized) {
      mixpanel.track(eventName, scrubProperties(properties), options, callback);
    } else if (env.IS_DEV) {
      console.log('[Analytics] Track:', eventName, '(properties hidden)');
    }
  }

  trackError(eventName: string, properties: Dict = {}) {
    this.track(eventName, { ...properties, is_error: true });
  }

  reset() {
    if (this.isInitialized) {
      mixpanel.reset();
      this.setPromoCode(undefined);
      this.registerBaseSuperProperties();
    }
  }
}

const analytics = new AnalyticsManager();

export default analytics;
